import { considerTimePeriodCategory } from './menu';
import { Category } from '../generated-interfaces/graphql';
import {
  IBrandMenu,
  ICategoryWithMealPeriod,
  IMUItem,
  IBrandMenuOrderConstraint,
  IBrandMenuOrderItem,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUnavailableEntity,
} from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
} from '../constants/brandMenu';
import { CartItem, CartModifierGroup } from './cart';
import {
  extractItemConversionBusinessRules,
  getRestaurantGroups,
  getTopLevelItems,
  mergeUnavailableEntitiesByName,
  parseCategoryAndMealPeriods,
  convertToMenuModuleOrder,
} from './menuUnification.utils';
import { UPSELL, AUTO_COMBOS } from '../constants';
import {
  UNIFIED_MENU_CODE_NAME_MAPPING,
  UNIFIED_MENU_MOD_SYMBOL_MAPPING,
} from './menuUnification.constants';
import logger from './logger';
import { convertToMapUsingName } from '.';

export const convertToBrandMenuCategories = (
  categories: Category[],
  brandMenu: IBrandMenu
) => {
  const brandMenuItems: string[] = [];
  brandMenu.items.forEach((item: IMUItem) => brandMenuItems.push(item.name));
  return [
    { ...categories[0], name: 'Support Brand Menu', menuItems: brandMenuItems },
  ];
};

export const getMenuModuleConstraints = (
  children: CartModifierGroup,
  cartItemsQuantity: Record<string, number>,
  forReadback: boolean
) => {
  const selectedItems = Object.values(children.selectedItems).reduce(
    (acc, item) => {
      acc.push(
        convertToMenuModuleOrder(item, cartItemsQuantity, true, forReadback)
      );
      return acc;
    },
    [] as IBrandMenuOrderItem[]
  );
  return {
    constraint_name: children.name,
    items: selectedItems as IBrandMenuOrderItem[],
  } as IBrandMenuOrderConstraint;
};

export const fillConstraints = (
  cartItem: CartItem,
  cartItemsQuantity: Record<string, number>,
  forReadback: boolean = false
) => {
  const { childModifierGroups } = cartItem;

  if (Object.values(childModifierGroups).length) {
    return Object.values(childModifierGroups).reduce((acc, child) => {
      acc.push(getMenuModuleConstraints(child, cartItemsQuantity, forReadback));
      return acc;
    }, [] as IBrandMenuOrderConstraint[]);
  }

  return undefined;
};

export const buildUnifiedMenuModuleOrder = (
  cartItem: CartItem,
  cartItemsQuantity: Record<string, number>,
  isModifier: boolean = false,
  forReadback: boolean = false
) => {
  const menuModuleItems = convertToMenuModuleOrder(
    cartItem,
    cartItemsQuantity,
    isModifier,
    forReadback
  );
  if (cartItem.name.includes(UPSELL)) {
    const upsellItem = menuModuleItems?.constraints?.[0]
      ?.items?.[0] as IBrandMenuOrderItem;
    if (!upsellItem) {
      return undefined;
    }
    return { ...upsellItem, quantity: menuModuleItems.quantity };
  }
  return menuModuleItems;
};

export const processBrandMenu = (
  brandMenu: IBrandMenu,
  restaurantCode: string,
  restaurantUnavailableEntities: IRestaurantUnavailableEntity[],
  formattedRestaurantMealPeriods: IRestaurantMealPeriod[],
  timezone: string = ''
) => {
  // Get unavailable items from both brand menu unavailable entities  and restaurant unavailable entities
  const brandUnavailableEntities = brandMenu.unavailable_entities;
  // restaurantCode parameter is already the correct restaurant code from the API response
  // (which may differ from the selected restaurant code when mirroring)
  const restaurantGroups = getRestaurantGroups(
    restaurantCode,
    brandMenu.restaurant_groups
  );

  const itemConversionBusinessRules = extractItemConversionBusinessRules(
    brandMenu.business_rules,
    brandMenu.restaurant_groups,
    restaurantCode
  );

  // Build unavailableItemsByName object
  /*
    Build unavailableItemsByName object by:
       - Using brand unavailable entities
       - Using restaurant unavailable entities
  */
  const unavailableItemsByName = {};
  mergeUnavailableEntitiesByName(
    brandUnavailableEntities.filter((unavailableEntity) =>
      restaurantGroups.includes(unavailableEntity.restaurant_group)
    ),
    unavailableItemsByName,
    timezone,
    true // Indicates that the unavailable entry is a brand menu unavailable one
  );
  mergeUnavailableEntitiesByName(
    restaurantUnavailableEntities as IUnavailableEntity[],
    unavailableItemsByName,
    timezone,
    false // Indicates that the unavailable entry is a restaurant unavailable one
  );

  const topLevelConstraints = brandMenu.constraints.filter(
    (constraint) => constraint.is_top_level
  ); // Constraint with is_top_level: true will replace category

  const itemsByName: Record<string, IMUItem> = {};
  const bmItemsSynonyms: Record<string, Array<string>> = {};

  brandMenu.items?.forEach((entry) => {
    const { name, customer_synonyms = [] } = entry;

    itemsByName[name] = entry;
    if (customer_synonyms.length) {
      bmItemsSynonyms[name] = [...customer_synonyms];
    }
  });

  // Process the meal period in items to get category with time period
  const restaurantMealPeriodsByName = convertToMapUsingName(
    formattedRestaurantMealPeriods
  );

  const categoriesWithMealPeriod: ICategoryWithMealPeriod[] =
    topLevelConstraints.length
      ? topLevelConstraints.reduce((acc, constraint) => {
          const { name, child_items, meal_period } = constraint;
          if (!child_items) return acc;

          const itemMealPeriods = Array.from(
            new Set<string>(
              child_items
                .map((itemName) => itemsByName[itemName]?.meal_period)
                .filter((mealPeriod): mealPeriod is string => !!mealPeriod)
            )
          );

          acc.push({
            name,
            item_names: child_items,
            mealPeriods: meal_period
              ? [...itemMealPeriods, meal_period]
              : [...itemMealPeriods],
          });

          logger.log({
            constraint,
            itemMealPeriods,
            categoryMealPeriod: meal_period,
            message: 'Handle top level constraint meal periods',
          });
          return acc;
        }, [] as ICategoryWithMealPeriod[])
      : brandMenu.categories.reduce((acc, category) => {
          if (!category?.item_names) return acc;

          const itemMealPeriods = Array.from(
            new Set<string>(
              category.item_names
                .map((itemName) => itemsByName[itemName]?.meal_period)
                .filter((mealPeriod): mealPeriod is string => !!mealPeriod)
            )
          );

          acc.push({
            ...category,
            mealPeriods: [...itemMealPeriods],
          });
          return acc;
        }, [] as ICategoryWithMealPeriod[]); // Adjust the type if necessary; TODO: remove category once the backend finishes transitioning

  const { categoriesWithTimePeriod, alwaysAvailableCategoryWithTimePeriods } =
    parseCategoryAndMealPeriods(
      categoriesWithMealPeriod,
      restaurantMealPeriodsByName
    );

  const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
    categoriesWithTimePeriod,
    timezone
  );
  const availableCategoryWithTimePeriod = categoriesWithActiveTimePeriod.filter(
    (category) => !category.inactive
  );

  const alwaysAvailableCategories = considerTimePeriodCategory(
    alwaysAvailableCategoryWithTimePeriods,
    timezone
  );

  // Populate the top level menu items using the active categories and unavailable items
  const topLevelMenuItems = getTopLevelItems(
    [
      ...categoriesWithActiveTimePeriod.filter(
        (category) => category.name !== AUTO_COMBOS
      ),
      ...alwaysAvailableCategories,
    ],
    itemsByName,
    unavailableItemsByName,
    convertToMapUsingName(brandMenu.constraints),
    timezone
  );

  return {
    topLevelMenuItems,
    brandMenu,
    restaurantCode,
    itemConversionBusinessRules,
    bmItemsSynonyms,
    restaurantUnavailableEntities,
    categoriesWithTimePeriod,
    availableCategoryWithTimePeriod,
    alwaysAvailableCategories,
    unavailableItemsByName,
    restaurantMealPeriodsByName,
    // Hardcode the following fields for compatibility
    processedCategoryAndTimePeriodJSON: {},
    processedMenuJSON: {},
    processedModGroupsJSON: {},
    persistentVoiceProps: {},
    codeNameMapping: UNIFIED_MENU_CODE_NAME_MAPPING,
    modSymbolMapping: UNIFIED_MENU_MOD_SYMBOL_MAPPING,
    unavailableItems: {},
    autoComboPrpIds: [],
    comboPrpIdToALaCartePrpId: {},
  };
};
